import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getStoredAuthUser, normalizeAuthUser, refreshStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import {
  Calendar,
  Camera,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  RefreshCw,
  Shield,
  Upload,
  User,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  changePassword,
  getApiCredentials,
  regenerateApiCredentials,
  updateProfile,
  type CompanyApiCredentials,
} from "@/services/authService";
import { toast } from "sonner";
import { isAxiosError } from "axios";

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

function formatCredentialsBlock( creds: CompanyApiCredentials): string {
  const lines = [
    `Name: ${creds.name}`,
    `Token URL: ${creds.token_url}`,
    `Grant type: ${creds.grant_type}`,
    `Client ID: ${creds.client_id}`,
    `Client secret: ${creds.client_secret ?? "(not available — regenerate secret)"}`,
    `Scope: ${creds.scopes.join(" ")}`,
    `API base URL: ${creds.api_base_url}`,
  ];
  return lines.join("\n");
}

const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

function formatIso(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function readPhotoAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function initialsFromName(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return "?";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

const Profile = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const [refreshing, setRefreshing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [updatingProfile, setUpdatingProfile] = useState(false);

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const avatarFileRef = useRef<File | null>(null);
  const [pendingRemoveAvatar, setPendingRemoveAvatar] = useState(false);
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
  const photoUploadInputRef = useRef<HTMLInputElement>(null);
  const captureVideoRef = useRef<HTMLVideoElement>(null);
  const captureStreamRef = useRef<MediaStream | null>(null);

  const [apiCredentials, setApiCredentials] = useState<CompanyApiCredentials | null>(null);
  const [apiCredentialsLoading, setApiCredentialsLoading] = useState(false);
  const [apiCredentialsError, setApiCredentialsError] = useState<string | null>(null);
  const [regeneratingSecret, setRegeneratingSecret] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copiedTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    setProfileName(user.name ?? "");
    setProfilePhone(user.phone ?? "");
  }, [user]);

  useEffect(() => {
    if (!user?.company_id) {
      setApiCredentials(null);
      setApiCredentialsError(null);
      return;
    }

    let cancelled = false;
    setApiCredentialsLoading(true);
    setApiCredentialsError(null);

    void (async () => {
      try {
        const data = await getApiCredentials();
        if (!cancelled) setApiCredentials(data);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        if (isAxiosError(err)) {
          const msg = (err.response?.data as { message?: string })?.message;
          setApiCredentialsError(msg || "Could not load API credentials.");
        } else {
          setApiCredentialsError("Could not load API credentials.");
        }
      } finally {
        if (!cancelled) setApiCredentialsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.company_id]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current != null) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const handleCopy = async (field: string, value: string) => {
    const ok = await copyText(value);
    if (!ok) {
      toast.error("Could not copy to clipboard.");
      return;
    }
    setCopiedField(field);
    if (copiedTimerRef.current != null) window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => setCopiedField(null), 1600);
    toast.success(field === "all" ? "API credentials copied." : "Copied.");
  };

  useEffect(() => {
    setAvatarPreview(null);
    setAvatarFile(null);
    avatarFileRef.current = null;
    setPendingRemoveAvatar(false);
  }, [user?.id, user?.avatar_url]);

  const roleLine = useMemo(() => {
    if (!user) return "—";
    if (user.role) return user.role;
    if (user.roles?.length) return user.roles.join(", ");
    return "—";
  }, [user]);

  const avatarDisplayUrl = pendingRemoveAvatar ? null : (avatarPreview ?? (user?.avatar_url?.trim() || null));
  const showRemoveAvatarBtn = Boolean(avatarDisplayUrl);

  const processAvatarFile = async (file: File | undefined) => {
    if (!file) return;
    const okType = /^image\/(jpeg|png)$/i.test(file.type) || /\.(jpe?g|png)$/i.test(file.name);
    if (!okType) {
      toast.error("Please choose a JPG or PNG image.");
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.error("Photo must be 5MB or smaller.");
      return;
    }
    avatarFileRef.current = file;
    try {
      const url = await readPhotoAsDataUrl(file);
      setAvatarPreview(url);
      setAvatarFile(file);
      setPendingRemoveAvatar(false);
    } catch {
      avatarFileRef.current = null;
      toast.error("Could not read the photo. Try another file.");
    }
  };

  const clearAvatar = () => {
    if (avatarFile || avatarFileRef.current) {
      setAvatarPreview(null);
      setAvatarFile(null);
      avatarFileRef.current = null;
      if (photoUploadInputRef.current) photoUploadInputRef.current.value = "";
      return;
    }
    if (user?.avatar_url?.trim()) {
      setPendingRemoveAvatar(true);
      setAvatarPreview(null);
    }
  };

  const stopCaptureStream = useCallback(() => {
    captureStreamRef.current?.getTracks().forEach((t) => t.stop());
    captureStreamRef.current = null;
    const v = captureVideoRef.current;
    if (v) v.srcObject = null;
  }, []);

  useEffect(() => {
    if (!captureDialogOpen) {
      stopCaptureStream();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera is not available in this browser. Use Upload Photo instead.");
      setCaptureDialogOpen(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        captureStreamRef.current = stream;
        const attach = () => {
          const el = captureVideoRef.current;
          if (!el) return false;
          el.srcObject = stream;
          void el.play().catch(() => {
            /* autoplay policies */
          });
          return true;
        };
        if (!attach()) {
          window.setTimeout(() => {
            if (!cancelled) attach();
          }, 100);
        }
      } catch {
        if (!cancelled) {
          toast.error("Could not access the camera. Allow permission or use Upload Photo.");
          setCaptureDialogOpen(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      stopCaptureStream();
    };
  }, [captureDialogOpen, stopCaptureStream]);

  const takePhotoFromCamera = () => {
    const video = captureVideoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      toast.error("Camera is not ready yet. Wait a moment and try again.");
      return;
    }
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;
    const size = Math.min(vw, vh);
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Could not capture image.");
          return;
        }
        if (blob.size > AVATAR_MAX_BYTES) {
          toast.error("Captured image is too large. Try Upload Photo with a smaller file.");
          return;
        }
        const file = new File([blob], `profile-avatar-${Date.now()}.jpg`, { type: "image/jpeg" });
        void processAvatarFile(file);
        setCaptureDialogOpen(false);
        stopCaptureStream();
      },
      "image/jpeg",
      0.92,
    );
  };

  const displayName = user?.name?.trim() || "—";

  return (
    <Layout title="Profile">
      <div className="w-full space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">User Profile</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Your account summary, basic details, password, and OAuth API credentials.
            </p>
          </div>
          <Button
            type="button"
            variant="glass"
            className="shrink-0 gap-2"
            disabled={refreshing}
            onClick={async () => {
              setRefreshing(true);
              try {
                const next = await refreshStoredAuthUser();
                setUser(next);
              } finally {
                setRefreshing(false);
              }
            }}
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>

        <div className="rounded-xl border border-border/60 bg-card/50 p-6 backdrop-blur-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <Avatar className="h-14 w-14 shrink-0 border-2 border-border/50 shadow-sm ring-2 ring-primary/10">
                {avatarDisplayUrl ? (
                  <AvatarImage src={avatarDisplayUrl} alt="" className="object-cover" />
                ) : null}
                <AvatarFallback className="rounded-full bg-primary/10 text-base font-semibold text-primary">
                  {initialsFromName(displayName === "—" ? "" : displayName)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 pt-0.5">
                <p className="truncate text-base font-semibold text-foreground">{user?.name ?? "—"}</p>
                <p className="truncate text-sm text-muted-foreground">{user?.email ?? "—"}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-emerald-600/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                Active
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/20 px-3 py-1 text-xs text-muted-foreground">
                <Shield className="h-3.5 w-3.5" />
                Role: <span className="font-medium text-foreground/90">{roleLine}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/20 px-3 py-1 text-xs text-muted-foreground">
                <Mail className="h-3.5 w-3.5" />
                Verified:{" "}
                <span className="font-medium text-foreground/90">{user?.email_verified_at ? "Yes" : "No"}</span>
              </span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border/60 bg-background/40 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Created</p>
              <p className="mt-1 flex items-center gap-2 text-sm font-medium text-foreground">
                <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{formatIso(user?.created_at)}</span>
              </p>
            </div>
            <div className="rounded-lg border border-border/60 bg-background/40 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Updated</p>
              <p className="mt-1 flex items-center gap-2 text-sm font-medium text-foreground">
                <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{formatIso(user?.updated_at)}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
          <div className="rounded-xl border border-border/60 bg-muted/25 p-6 dark:bg-muted/15">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">Change password</h2>
              <p className="mt-1 text-sm text-muted-foreground">Enter your current password, then choose a new one.</p>
            </div>

            <form
              className="mt-5 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (updatingPassword) return;
                if (!currentPassword.trim()) {
                  toast.error("Enter your current password.");
                  return;
                }
                if (!newPassword.trim()) {
                  toast.error("Enter a new password.");
                  return;
                }
                if (newPassword !== confirmPassword) {
                  toast.error("New password and confirm password do not match.");
                  return;
                }
                setUpdatingPassword(true);
                try {
                  await changePassword({
                    current_password: currentPassword,
                    password: newPassword,
                    password_confirmation: confirmPassword,
                  });
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                  toast.success("Password updated.");
                } catch (err) {
                  console.error(err);
                  toast.error("Could not update password.");
                } finally {
                  setUpdatingPassword(false);
                }
              }}
            >
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Current password</Label>
                <div className="relative">
                  <Input
                    className="glass-input h-10 pr-10"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">New password</Label>
                <div className="relative">
                  <Input
                    className="glass-input h-10 pr-10"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Confirm new password</Label>
                <div className="relative">
                  <Input
                    className="glass-input h-10 pr-10"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <Button type="submit" variant="premium" className="gap-2" disabled={updatingPassword}>
                  {updatingPassword ? "Updating..." : "Update password"}
                </Button>
              </div>
            </form>
          </div>

          <div className="rounded-xl border border-border/60 bg-card/70 p-6 backdrop-blur-sm dark:bg-card/40">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">Basic information</h2>
              <p className="mt-1 text-sm text-muted-foreground">Photo, name, and phone. Email cannot be changed here.</p>
            </div>

            <form
              className="mt-5 space-y-6"
              onSubmit={async (e) => {
                e.preventDefault();
                if (updatingProfile) return;
                const name = profileName.trim();
                if (!name) {
                  toast.error("Name is required.");
                  return;
                }
                setUpdatingProfile(true);
                try {
                  const fileForUpload = avatarFileRef.current ?? avatarFile;
                  const res = await updateProfile({
                    name,
                    phone: profilePhone.trim() || null,
                    avatar: fileForUpload ?? undefined,
                    removeAvatar: pendingRemoveAvatar && !fileForUpload,
                  });
                  const next = normalizeAuthUser(res);
                  if (next) {
                    localStorage.setItem("auth_user", JSON.stringify(next));
                    window.dispatchEvent(new Event("auth_user_updated"));
                    setUser(next);
                  }
                  setAvatarPreview(null);
                  setAvatarFile(null);
                  avatarFileRef.current = null;
                  setPendingRemoveAvatar(false);
                  if (photoUploadInputRef.current) photoUploadInputRef.current.value = "";
                  toast.success("Profile updated.");
                } catch (err) {
                  console.error(err);
                  if (isAxiosError(err)) {
                    const msg = (err.response?.data as { message?: string })?.message;
                    toast.error(msg || "Could not update profile.");
                  } else {
                    toast.error("Could not update profile.");
                  }
                } finally {
                  setUpdatingProfile(false);
                }
              }}
            >
              <div className="rounded-lg border border-border/50 bg-muted/15 p-4 dark:bg-muted/10">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Profile photo</p>
                <input
                  ref={photoUploadInputRef}
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="sr-only"
                  onChange={(e) => {
                    void processAvatarFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                  <div className="relative mx-auto shrink-0 sm:mx-0">
                    <div
                      className={cn(
                        "flex h-[100px] w-[100px] sm:h-[120px] sm:w-[120px] items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-border/60 bg-muted/25",
                        avatarDisplayUrl && "border-solid border-primary/25 shadow-inner",
                      )}
                    >
                      {avatarDisplayUrl ? (
                        <img src={avatarDisplayUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <User className="h-12 w-12 text-muted-foreground/35 sm:h-14 sm:w-14" strokeWidth={1.25} />
                      )}
                    </div>
                    {showRemoveAvatarBtn ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="secondary"
                        className="absolute -right-1 -top-1 h-7 w-7 rounded-full border border-border shadow-md"
                        onClick={clearAvatar}
                        aria-label="Remove photo"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col items-center gap-3 sm:items-start">
                    <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
                      <Button type="button" variant="secondary" className="h-10 gap-2" onClick={() => photoUploadInputRef.current?.click()}>
                        <Upload className="h-4 w-4" />
                        Upload photo
                      </Button>
                      <Button type="button" variant="secondary" className="h-10 gap-2" onClick={() => setCaptureDialogOpen(true)}>
                        <Camera className="h-4 w-4" />
                        Capture
                      </Button>
                    </div>
                    <p className="text-center text-xs text-muted-foreground sm:text-left">JPG or PNG, up to 5MB. Square photos look best.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="profile-name" className="text-xs text-muted-foreground">
                    Name
                  </Label>
                  <Input
                    id="profile-name"
                    className="glass-input h-10"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    autoComplete="name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-phone" className="text-xs text-muted-foreground">
                    Phone
                  </Label>
                  <Input
                    id="profile-phone"
                    className="glass-input h-10"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    autoComplete="tel"
                    placeholder="Optional"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-email" className="text-xs text-muted-foreground">
                    Email
                  </Label>
                  <Input id="profile-email" className="glass-input h-10" value={user?.email ?? ""} readOnly />
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <Button type="submit" variant="premium" className="gap-2" disabled={updatingProfile}>
                  {updatingProfile ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {user?.company_id ? (
          <div className="rounded-xl border border-border/60 bg-card/50 p-6 backdrop-blur-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                  OAuth API credentials
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Use these client credentials to request a token and call the external API for your company.
                </p>
              </div>
              <Button
                type="button"
                variant="glass"
                className="shrink-0 gap-2"
                disabled={regeneratingSecret || apiCredentialsLoading}
                onClick={async () => {
                  if (regeneratingSecret) return;
                  setRegeneratingSecret(true);
                  try {
                    const data = await regenerateApiCredentials();
                    setApiCredentials(data);
                    setShowClientSecret(true);
                    toast.success("Client secret regenerated.");
                  } catch (err) {
                    console.error(err);
                    toast.error("Could not regenerate client secret.");
                  } finally {
                    setRegeneratingSecret(false);
                  }
                }}
              >
                <RefreshCw className={cn("h-4 w-4", regeneratingSecret && "animate-spin")} />
                {regeneratingSecret ? "Regenerating..." : "Regenerate secret"}
              </Button>
            </div>

            {apiCredentialsLoading ? (
              <p className="mt-5 text-sm text-muted-foreground">Loading credentials…</p>
            ) : apiCredentialsError ? (
              <p className="mt-5 text-sm text-destructive">{apiCredentialsError}</p>
            ) : apiCredentials ? (
              <div className="mt-5 space-y-3">
                {(
                  [
                    { key: "name", label: "Client name", value: apiCredentials.name },
                    { key: "token_url", label: "Token URL", value: apiCredentials.token_url },
                    { key: "grant_type", label: "Grant type", value: apiCredentials.grant_type },
                    { key: "client_id", label: "Client ID", value: apiCredentials.client_id },
                    {
                      key: "client_secret",
                      label: "Client secret",
                      value: apiCredentials.client_secret,
                      secret: true,
                    },
                    { key: "scopes", label: "Scope", value: apiCredentials.scopes.join(" ") },
                    { key: "api_base_url", label: "API base URL", value: apiCredentials.api_base_url },
                  ] as const
                ).map((row) => {
                  const isSecret = "secret" in row && row.secret;
                  const rawValue = row.value;
                  const hasValue = typeof rawValue === "string" && rawValue.length > 0;
                  const displayValue = !hasValue
                    ? "Not available — regenerate secret to create one"
                    : isSecret && !showClientSecret
                      ? "••••••••••••••••••••••••••••••••••••••••"
                      : rawValue;

                  return (
                    <div
                      key={row.key}
                      className="rounded-lg border border-border/60 bg-background/40 p-3 sm:p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {row.label}
                          </p>
                          <p className="mt-1 break-all font-mono text-sm text-foreground">{displayValue}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {isSecret && hasValue ? (
                            <button
                              type="button"
                              onClick={() => setShowClientSecret((s) => !s)}
                              className="rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground"
                              aria-label={showClientSecret ? "Hide client secret" : "Show client secret"}
                            >
                              {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            disabled={!hasValue}
                            onClick={() => void handleCopy(row.key, rawValue ?? "")}
                            className="rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                            aria-label={`Copy ${row.label}`}
                          >
                            {copiedField === row.key ? (
                              <Check className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="flex flex-col gap-2 border-t border-border/50 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">
                    Copy all fields below for use in your integration or password manager.
                  </p>
                  <Button
                    type="button"
                    variant="premium"
                    className="gap-2"
                    onClick={() => void handleCopy("all", formatCredentialsBlock(apiCredentials))}
                  >
                    {copiedField === "all" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    Copy these API credentials
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <Dialog
          open={captureDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              stopCaptureStream();
              setCaptureDialogOpen(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Capture photo</DialogTitle>
            </DialogHeader>
            <div className="mx-auto aspect-square w-full max-w-[min(100%,420px)] overflow-hidden rounded-xl bg-black">
              <video ref={captureVideoRef} className="h-full w-full object-cover" autoPlay playsInline muted />
            </div>
            <p className="text-xs text-muted-foreground">Allow camera access, then tap Take photo when you are in frame.</p>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="glass"
                className="w-full sm:w-auto"
                onClick={() => {
                  stopCaptureStream();
                  setCaptureDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="button" variant="premium" className="w-full gap-2 sm:w-auto" onClick={takePhotoFromCamera}>
                <Camera className="h-4 w-4" />
                Take photo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Profile;
